import { supabase } from "@/integrations/supabase/client";
import { updateItemCategories } from "@/services/menu/categoryService";
import { updateItemTags } from "@/services/menu/tagService";
import { logActivityEvent } from "@/services/activity/activityLogService";
import { MenuItemFormData } from "@/types/menu";
import { MenuItemCreateData } from "@/models/menuItemModel";
import { toast } from "@/hooks/use-toast";

export const createMenuItem = async (
  data: MenuItemFormData | MenuItemCreateData,
  categoryIds: string[] = [],
  tagNames: string[] = []
): Promise<string | null> => {
  try {
    // Extract the menu item data without categoryIds and tags
    const { categoryIds: _, tags: __, ...menuItemData } = data as any;

    // Convert price to number if it's a string
    const itemToInsert = {
      ...menuItemData,
      price:
        typeof menuItemData.price === "string"
          ? parseFloat(menuItemData.price)
          : menuItemData.price,
      duration_minutes: menuItemData.duration_minutes || null,
      max_advance_booking_days: menuItemData.max_advance_booking_days || 30,
    };

    // Remove categoryId if it exists (it's not a column in the menu_items table)
    if ("categoryId" in itemToInsert) {
      delete itemToInsert.categoryId;
    }

    // Insert the menu item
    const { data: createdItem, error } = await supabase
      .from("menu_items")
      .insert(itemToInsert)
      .select()
      .single();

    if (error) {
      console.error("Error creating menu item:", error);
      throw new Error(`Failed to create menu item: ${error.message}`);
    }

    // Update categories
    if (categoryIds.length > 0) {
      await updateItemCategories(createdItem.id, categoryIds);
    }

    // Update tags
    if (tagNames.length > 0) {
      await updateItemTags(createdItem.id, tagNames);
    }

    // Log activity
    await logActivityEvent({
      entity_type: "menu_item",
      entity_id: createdItem.id,
      user_name: "Admin",
      action: "menu_item_created",
      details: {
        name: createdItem.name,
        price: createdItem.price,
        categories: categoryIds,
        tags: tagNames,
      },
      store_id: createdItem.store_id,
    });

    return createdItem.id;
  } catch (error) {
    console.error("Error in createMenuItem:", error);
    throw error;
  }
};

// Helper function to get or create tag IDs from tag names
async function getOrCreateTagIds(tagIds: string[]): Promise<string[]> {
  if (!tagIds || tagIds.length === 0) return [];

  // First try to get existing tags
  const { data: existingTags, error: fetchError } = await supabase
    .from("menu_tags")
    .select("id, name")
    .in("id", tagIds);

  if (fetchError) {
    console.error("Error fetching existing tags:", fetchError);
    throw new Error(`Failed to fetch existing tags: ${fetchError.message}`);
  }

  // Create a map of existing tag names to IDs
  const existingTagMap = new Map();
  if (existingTags) {
    existingTags.forEach((tag) => {
      existingTagMap.set(tag.name, tag.id);
    });
  }

  // // For each tag name, get ID from map or create new tag
  // for (const tagId of tagIds) {
  //   if (existingTagMap.has(tagId)) {
  //     tagIds.push(existingTagMap.get(tagId));
  //   } else {
  //     // Create new tag if it doesn't exist
  //     const { data: newTag, error: createError } = await supabase
  //       .from("menu_tags")
  //       .insert({ id: tagId })
  //       .select()
  //       .single();

  //     if (createError) {
  //       console.error("Error creating tag:", createError);
  //       throw new Error(
  //         `Failed to create tag "${tagId}": ${createError.message}`
  //       );
  //     } else if (newTag) {
  //       tagIds.push(newTag.id);
  //     }
  //   }
  //}

  return tagIds;
}
